import { ArrowRight } from "lucide-react";
import { fechaCorta, primerNombre } from "@/reclutamiento/logica";
import type { VacanteInicio } from "@/reclutamiento/tipos";
import { BannerPago, CajaError, Esqueleto } from "../componentes";
import { usePortal } from "../contexto";
import { IconoEtapa, Mk, Nuevo } from "../iconos";
import { PESTANA_VISUAL } from "../etapas";
import { ruta } from "../ruta";
import { claveCache, useCarga } from "../util";

export function Inicio() {
  const { api, sesion } = usePortal();
  const carga = useCarga(() => api.inicio(), [api], undefined, claveCache(sesion, api.verComo, "inicio"));
  const nombre = primerNombre(sesion.usuario?.nombre ?? sesion.cliente.contacto_nombre) || "";

  return (
    <>
      <div className="phello">
        <p className="eyebrow">{sesion.cliente.nombre}</p>
        <h1>{nombre ? `Hola, ${nombre}` : "Hola"}</h1>
        <p>
          {sesion.solo_lectura
            ? "Así ve su portal el cliente."
            : sesion.ultima_visita
              ? `Tu última visita fue el ${fechaCorta(sesion.ultima_visita)}.`
              : "Es tu primera visita. Te damos la bienvenida."}
        </p>
      </div>
      <BannerPago />
      {carga.cargando && !carga.datos ? (
        <Esqueleto filas={2} alto={250} />
      ) : carga.error ? (
        <CajaError texto={carga.error} onReintentar={carga.recargar} />
      ) : !carga.datos?.length ? (
        <p className="empty">
          Aún no tienes vacantes activas. En cuanto firmes los criterios de tu vacante y arranque el anuncio, aquí vas a ver a
          tus candidatos.
        </p>
      ) : (
        <div className="vgrid">
          {carga.datos.map((v) => (
            <TarjetaVacante key={v.id} v={v} />
          ))}
        </div>
      )}
    </>
  );
}

function TarjetaVacante({ v }: { v: VacanteInicio }) {
  // Cada número abre Candidatos con exactamente eso: la vacante, la pestaña y, si aplica, la etapa o los nuevos.
  // Con «todas» las plazas, para que lo que sale cuadre con el número que se picó.
  const en = (grupo: string, extra: Record<string, string> = {}) => ruta("candidatos", { vacante: v.id, grupo, plaza: "todas", ...extra });
  return (
    <article className="vcard" aria-labelledby={`vi-${v.id}`}>
      <div className="vh">
        <div>
          <h2 id={`vi-${v.id}`}>{v.puesto}</h2>
          <p>
            {v.zona} · {v.activos} {v.activos === 1 ? "candidato activo" : "candidatos activos"}
          </p>
        </div>
      </div>
      <a className={"bign" + (v.nuevos > 0 ? " hay" : "")} href={en("activos", { nuevos: "1" })} aria-label={`Ver los ${v.nuevos} nuevos desde tu última visita`}>
        {v.nuevos > 0 && <Nuevo />}
        <span className="num">{v.nuevos}</span>
        <span>{v.nuevos === 1 ? "nuevo" : "nuevos"} desde tu última visita</span>
        {v.nuevos > 0 && (
          <span className="bign-ver">
            {v.nuevos === 1 ? "Ver al nuevo" : `Ver los ${v.nuevos}`} <ArrowRight size={16} aria-hidden="true" />
          </span>
        )}
      </a>
      <div className="triad" aria-label="Candidatos activos por etapa">
        <a className="neu c-nuevo" href={en("activos", { etapa: "nuevo" })} aria-label={`Ver los ${v.sin_contactar} sin contactar`}>
          <span className="num">
            <IconoEtapa c="nuevo" s={32} />
            {v.sin_contactar}
          </span>
          Sin contactar
          <Ver n={v.sin_contactar} />
        </a>
        <a className="neu c-contactado" href={en("activos", { etapa: "contactado" })} aria-label={`Ver los ${v.contactados} contactados`}>
          <span className="num">
            <IconoEtapa c="contactado" s={32} />
            {v.contactados}
          </span>
          {v.contactados === 1 ? "Contactado" : "Contactados"}
          <Ver n={v.contactados} />
        </a>
        <a className="neu c-entrevistado" href={en("activos", { etapa: "entrevistado" })} aria-label={`Ver los ${v.en_entrevista} entrevistados`}>
          <span className="num">
            <IconoEtapa c="entrevistado" s={32} />
            {v.en_entrevista}
          </span>
          {v.en_entrevista === 1 ? "Entrevistado" : "Entrevistados"}
          <Ver n={v.en_entrevista} />
        </a>
      </div>
      {v.sin_contactar > 0 && (
        <p className="row" style={{ fontSize: 13.5, gap: 12 }}>
          <span className="row" style={{ gap: 5 }}>
            <Mk k="ok" s={20} />
            <b className="num">{v.sin_contactar_cumple}</b> {v.sin_contactar_cumple === 1 ? "cumple" : "cumplen"}
          </span>
          <span className="row" style={{ gap: 5 }}>
            <Mk k="yel" s={20} />
            <b className="num">{v.sin_contactar_revisar}</b> a revisar (les falta algo)
          </span>
          <span className="muted">de los sin contactar</span>
        </p>
      )}
      {v.por_marcar > 0 && (
        <p style={{ fontSize: 14 }}>
          <a className="enl" href={en("activos", { etapa: "contactado" })}>
            <b className="num">{v.por_marcar}</b> te {v.por_marcar === 1 ? "falta" : "faltan"} por marcar
          </a>{" "}
          como entrevistado o contratado.
        </p>
      )}
      <div className="rep">
        <a href={en("algun_dia")} className="c-algun_dia">
          <IconoEtapa c="algun_dia" s={24} />
          Algún día <b className="num">{v.algun_dia}</b>
        </a>
        <a href={en("contratados")} className="c-contratado">
          <IconoEtapa c="contratado" s={24} />
          Contratados <b className="num">{v.contratados}</b>
        </a>
        <a href={en("descartados")} className="c-descartado">
          <IconoEtapa c="descartado" s={24} />
          Descartados <b className="num">{v.descartados}</b>
        </a>
        <a href={en("todos")} className={PESTANA_VISUAL.todos.clase}>
          Total <b className="num">{v.total}</b>
        </a>
      </div>
      <a className="btn primary" href={en("todos")}>
        Ver todos mis candidatos <ArrowRight size={16} aria-hidden="true" />
      </a>
    </article>
  );
}

/** «Ver →» debajo de cada número: que se note que se puede picar. */
function Ver({ n }: { n: number }) {
  return (
    <span className="triad-ver" aria-hidden="true">
      {n === 0 ? "Ver" : n === 1 ? "Verlo" : `Ver los ${n}`} <ArrowRight size={14} />
    </span>
  );
}
